/**
 * Dashboard UI Types - Frontend-specific extensions for dashboard protobuf types
 * 
 * This file extends protobuf-generated types with UI-specific properties
 * and provides additional types needed for frontend components.
 */

// Import protobuf types for extension
import {
  DashboardEntity as PBDashboardEntity,
  WidgetEntity as PBWidgetEntity,
  DashboardCreateRequest as PBDashboardCreateRequest,
  WidgetCreateRequest as PBWidgetCreateRequest,
  DashboardTemplate as PBDashboardTemplate,
} from '../api/dashboard/dashboard.pb';

import { ObjectEntity } from '../api/object/object.pb';
import { DeviceEntity } from '../api/device/device.pb';
import { ParamEntity } from '../api/param/param.pb';

// Re-export protobuf types as base types
export {
  DashboardEntity,
  WidgetEntity,
  WidgetTypeEntity,
  DashboardCreateRequest,
  DashboardUpdateRequest,
  WidgetCreateRequest,
  WidgetUpdateRequest,
  WidgetPositionUpdate,
  DashboardWidgetsPositionRequest,
  CreateDashboardFromTemplateRequest,
  DashboardTemplate,
  DashboardTemplateConfig,
  WidgetTemplateConfig,
  SearchDashboardsRequest,
  SearchDashboardsResponse,
  DashboardStats,
  ValidateWidgetRequest,
  ValidateWidgetResponse,
  GetWidgetTypeSchemaResponse,
} from '../api/dashboard/dashboard.pb';

// =======================
// UI-SPECIFIC EXTENSIONS
// =======================

/**
 * React Grid Layout configuration for dashboard widgets
 */
export interface DashboardLayout {
  i: string; // widget id
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
}

/**
 * Widget types supported by the dashboard
 */
export type WidgetType = 'chart' | 'table' | 'gauge' | 'text' | 'schema' | 'map';

/**
 * Enhanced Dashboard entity with UI-specific properties
 */
export interface Dashboard extends Omit<PBDashboardEntity, 'widgets'> {
  widgets: DashboardWidget[];
  layout?: DashboardLayout[]; // For react-grid-layout
}

/**
 * Enhanced Widget entity with UI-specific properties
 * Note: Backend may return config and data_source as already parsed objects
 * instead of JSON strings, despite protobuf definitions
 */
export interface DashboardWidget extends Omit<PBWidgetEntity, 'config' | 'data_source'> {
  // Override config and data_source to accept both string and object
  config: string | any;
  data_source: string | any;
}

/**
 * Enhanced Dashboard Template with UI-specific properties
 */
export interface DashboardTemplateUI extends PBDashboardTemplate {
  preview_image?: string;
  layout?: DashboardLayout[];
  widgets?: any[]; // Add widgets property for compatibility
}

// =======================
// WIDGET CONFIGURATIONS
// =======================

/**
 * Data source item for widgets
 */
export interface DataSourceItem {
  deviceId: string;
  archiveTypeId: string;
  paramId: string;
  // Дополнительные поля для отображения
  deviceName?: string;
  deviceImei?: string;
  deviceObjectName?: string;
  archiveTypeName?: string;
  paramName?: string;
  paramVarname?: string;
  paramLabel?: string;
  paramUnit?: string;
}

/**
 * Chart widget configuration
 */
export interface ChartWidgetConfig {
  chart_type: string;
  chartType?: string;
  data_source: string;
  dataSource?: {
    items: DataSourceItem[];
  };
  timeRange?: {
    start: string;
    end: string;
  };
  xAxis?: {
    title: string;
  };
  yAxis?: {
    title: string;
  };
  title?: string;
  refreshInterval?: number;
}

/**
 * Table widget configuration
 */
export interface TableWidgetConfig {
  data_source?: string; // Старый формат (для обратной совместимости)
  
  // Новый формат с источниками данных
  dataSource?: {
    items: DataSourceItem[];
  };
  
  // Старый формат (для обратной совместимости)
  columns?: TableColumn[];
  
  pageSize?: number;
  showPagination?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  refreshInterval?: number;
}

export interface TableColumn {
  key: string;
  field: string;
  title: string;
  type: 'text' | 'number' | 'date' | 'boolean';
  width?: number;
  sortable?: boolean;
  filterable?: boolean;
}

/**
 * Gauge widget configuration
 */
export interface GaugeWidgetConfig {
  // Старый формат (для обратной совместимости)
  param_id?: string;
  paramId?: string;
  deviceId?: string;
  archiveTypeId?: string;
  
  // Новый формат с источником данных (один параметр)
  dataSource?: {
    deviceId: string;
    archiveTypeId: string;
    paramId: string;
    // Дополнительные поля для отображения
    deviceName?: string;
    deviceImei?: string;
    deviceObjectName?: string;
    archiveTypeName?: string;
    paramName?: string;
    paramVarname?: string;
    paramUnit?: string;
  };
  
  min_value?: number;
  minValue?: number;
  max_value?: number;
  maxValue?: number;
  unit?: string;
  format?: string;
  thresholds?: Array<{
    value: number;
    color: string;
    label?: string;
  }>;
  refreshInterval?: number;
}

/**
 * Text widget configuration
 */
export interface TextWidgetConfig {
  content: string;
  fontColor?: string;
  backgroundColor?: string;
  fontSize?: number;
  fontWeight?: string;
  color?: string;
  alignment?: 'left' | 'center' | 'right';
}

/**
 * Schema widget configuration
 */
export interface SchemaWidgetConfig {
  dataSource?: {
    items: DataSourceItem[];
  };
  schema_content?: string;
  objectMappings?: any[];
  schemaData?: any;
}

/**
 * Map widget configuration
 */
export interface MapWidgetConfig {
  dataSource?: {
    items: DataSourceItem[];
  };
  center?: {
    lat: number;
    lng: number;
  };
  zoom?: number;
}

// =======================
// ENHANCED DATA TYPES
// =======================

/**
 * Extended object data for widgets
 */
export interface ObjectData extends ObjectEntity {
  type?: string;
  company_id?: string;
  location?: {
    lat: number;
    lng: number;
  };
  status?: string;
}

/**
 * Extended device data for widgets
 */
export interface DeviceData extends DeviceEntity {
  type?: string;
  status?: 'online' | 'offline' | 'error';
  location?: {
    lat: number;
    lng: number;
  };
}

/**
 * Extended parameter data for widgets
 */
export interface ParamData extends ParamEntity {
  device_id?: string;
  type?: 'number' | 'string' | 'boolean';
  unit?: string;
  value?: any;
  timestamp?: string;
}

// =======================
// API RESPONSE TYPES
// =======================

export interface ApiResponse<T> {
  data: T;
  success?: boolean;
  message?: string;
  total?: number;
  page?: number;
  per_page?: number;
  total_pages?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface DashboardsResponse {
  data: Dashboard[];
  total: number;
  page: number;
  per_page: number;
}

// =======================
// ENHANCED REQUEST TYPES
// =======================

/**
 * Enhanced dashboard creation request with UI-specific properties
 */
export interface CreateDashboardRequest extends PBDashboardCreateRequest {
  layout?: DashboardLayout[];
}

/**
 * Enhanced widget creation request with UI-specific properties
 */
export interface CreateWidgetRequest extends PBWidgetCreateRequest {
  type?: WidgetType; // Optional for backwards compatibility
  layout?: DashboardLayout; // Optional for backwards compatibility
}

export interface UpdateWidgetRequest extends Partial<CreateWidgetRequest> {
  id: string;
}

export interface UpdateDashboardRequest extends Partial<CreateDashboardRequest> {
  id: string;
  widgets?: DashboardWidget[];
}

export interface UpdateDashboardData extends Partial<CreateDashboardRequest> {
  widgets?: DashboardWidget[];
}

export interface CreateFromTemplateRequest {
  template_id: string;
  name: string;
  description?: string;
  is_public?: boolean;
}

// =======================
// QUERY TYPES
// =======================

export interface DashboardsQuery {
  page?: number;
  per_page?: number;
  search?: string;
  is_public?: boolean;
  user_id?: string;
  company_id?: string;
}

// =======================
// DATA SOURCE TYPES
// =======================

/**
 * Base data source interface
 */
export interface BaseDataSource {
  // Common fields
}

/**
 * Chart widget data source
 */
export interface ChartDataSource extends BaseDataSource {
  param_ids: string[];
  aggregation?: 'avg' | 'min' | 'max' | 'sum' | 'count';
  interval?: '1m' | '5m' | '15m' | '1h' | '6h' | '24h';
}

/**
 * Table widget data source
 */
export interface TableDataSource extends BaseDataSource {
  data_type: 'objects' | 'devices' | 'params' | 'alarms' | 'users';
  filters?: {
    company_id?: string;
    object_id?: string;
    search?: string;
  };
}

/**
 * Map widget data source
 */
export interface MapDataSource extends BaseDataSource {
  data_type: 'objects' | 'devices';
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  filters?: {
    company_id?: string;
    object_types?: string[];
    device_types?: string[];
  };
}

/**
 * Gauge widget data source
 */
export interface GaugeDataSource extends BaseDataSource {
  param_id: string;
  realtime?: boolean;
}

/**
 * Text widget data source (minimal/none)
 */
export interface TextDataSource extends BaseDataSource {
  // Text widgets typically don't need external data
}

/**
 * Schema widget data source
 */
export interface SchemaDataSource extends BaseDataSource {
  data_type: 'objects' | 'devices';
  object_ids?: string[];
  device_ids?: string[];
}

/**
 * Widget data source union type
 */
export type WidgetDataSource = 
  | ChartDataSource
  | TableDataSource 
  | MapDataSource
  | GaugeDataSource
  | TextDataSource
  | SchemaDataSource;

// =======================
// UTILITY FUNCTIONS
// =======================

/**
 * Create default data source for widget type
 */
export function createDefaultDataSource(type: WidgetType): WidgetDataSource {
  switch (type) {
    case 'chart':
      return {
        param_ids: [], // Пустой массив, будет настроен пользователем
        aggregation: 'avg',
        interval: '1h',
      } as ChartDataSource;
    
    case 'table':
      return {
        data_type: 'objects',
        filters: {},
      } as TableDataSource;
    
    case 'map':
      return {
        data_type: 'objects',
        filters: {},
      } as MapDataSource;
    
    case 'gauge':
      return {
        param_id: '', // Пустая строка, будет настроена пользователем
        realtime: true,
      } as GaugeDataSource;
    
    case 'text':
      return {} as TextDataSource;
    
    case 'schema':
      return {
        data_type: 'objects',
        object_ids: [],
      } as SchemaDataSource;
    
    default:
      return {} as BaseDataSource;
  }
}

/**
 * Widget type to ID mapping (based on database migration)
 */
export const WIDGET_TYPE_IDS = {
  chart: '10000000-0000-0000-0000-000000000001',
  map: '10000000-0000-0000-0000-000000000002',
  table: '10000000-0000-0000-0000-000000000003',
  schema: '10000000-0000-0000-0000-000000000004',
  gauge: '10000000-0000-0000-0000-000000000005',
  text: '10000000-0000-0000-0000-000000000006',
} as const;

/**
 * Convert WidgetType to widget_type_id
 */
export function getWidgetTypeId(type: WidgetType): string {
  return WIDGET_TYPE_IDS[type] || '';
}

/**
 * Convert widget_type_id to WidgetType
 */
export function getWidgetTypeFromId(id: string): WidgetType | undefined {
  const entry = Object.entries(WIDGET_TYPE_IDS).find(([, typeId]) => typeId === id);
  return entry ? (entry[0] as WidgetType) : undefined;
}

/**
 * Get widget type from WidgetEntity
 */
export function getWidgetType(widget: PBWidgetEntity): WidgetType | undefined {
  // First try to get from widget_type.varname if available
  if (widget.widget_type?.varname) {
    return isValidWidgetType(widget.widget_type.varname) ? widget.widget_type.varname as WidgetType : undefined;
  }
  
  // Fallback to widget_type_id mapping
  return getWidgetTypeFromId(widget.widget_type_id);
}

// =======================
// TYPE GUARDS
// =======================

export function isDashboard(obj: any): obj is Dashboard {
  return obj && typeof obj.id === 'string' && typeof obj.name === 'string';
}

export function isDashboardWidget(obj: any): obj is DashboardWidget {
  return obj && typeof obj.id === 'string' && typeof obj.title === 'string' && obj.type;
}

export function isValidWidgetType(type: string): type is WidgetType {
  return ['chart', 'table', 'gauge', 'text', 'schema', 'map'].includes(type);
}

// =======================
// UTILITY TYPES
// =======================

/**
 * Widget configuration union type
 */
export type WidgetConfig = 
  | ChartWidgetConfig
  | TableWidgetConfig 
  | GaugeWidgetConfig
  | TextWidgetConfig
  | SchemaWidgetConfig
  | MapWidgetConfig;

/**
 * Widget data union type
 */
export type WidgetData = ObjectData | DeviceData | ParamData;